// packages/web/src/components/AdminOrders.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ShoppingBag,
  Search,
  Eye,
  Truck,
  X,
} from 'lucide-react'
import { DataTablePagination } from './DataTablePagination'
import { AppDataTable } from './AppDataTable'
import { SearchableSelect } from './SearchableSelect'
import { apiGet, apiGetWithMeta } from '../lib/api'
import type { Order, Customer } from '@buddys-choice/shared'

function formatCurrency(amt: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)
}

function formatOrderDate(value: string | undefined) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

function statusLabel(status: string) {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
]

export const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [trackingFilter, setTrackingFilter] = useState('')
  const [cityFilter, setCityFilter] = useState('')
  const [customerFilter, setCustomerFilter] = useState('')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  useEffect(() => {
    apiGet<Customer[]>('customers', { pageSize: 200 })
      .then((data) => setCustomers(Array.isArray(data) ? data : []))
      .catch(() => setCustomers([]))
  }, [])

  const fetchOrders = useCallback(() => {
    setLoading(true)
    apiGetWithMeta<Order[]>('orders', {
      search: search || undefined,
      status: statusFilter || undefined,
      trackingId: trackingFilter || undefined,
      city: cityFilter || undefined,
      customerId: customerFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      page,
      pageSize: 10,
      sortBy: 'created_at',
      sortDir: 'desc',
    })
      .then((res) => {
        setOrders(res.data || [])
        setTotal(res.meta?.total ?? 0)
        if (res.meta?.totalPages) setTotalPages(res.meta.totalPages)
      })
      .catch((err) => console.error('Fetch orders error:', err))
      .finally(() => setLoading(false))
  }, [search, statusFilter, trackingFilter, cityFilter, customerFilter, fromDate, toDate, page])

  useEffect(() => {
    if (!customerFilter) {
      setSelectedCustomer(null)
      return
    }
    const match = customers.find((c) => c.id === customerFilter)
    if (match) {
      setSelectedCustomer(match)
      return
    }
    apiGet<Customer & { orders?: Order[] }>(`customers/${customerFilter}`)
      .then((data) => setSelectedCustomer(data))
      .catch(() => setSelectedCustomer(null))
  }, [customerFilter, customers])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('')
    setTrackingFilter('')
    setCityFilter('')
    setCustomerFilter('')
    setFromDate('')
    setToDate('')
    setPage(1)
  }

  const hasActiveFilters =
    Boolean(search || statusFilter || trackingFilter || cityFilter || customerFilter || fromDate || toDate)

  const customerOptions = useMemo(
    () => [
      { value: '', label: 'All customers' },
      ...customers.map((c) => ({
        value: c.id,
        label: `${c.name} (${c.phone})`,
        keywords: `${c.phone} ${c.email ?? ''}`,
      })),
    ],
    [customers],
  )

  const statusOptions = useMemo(
    () => STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
    [],
  )

  const orderColumns = useMemo(
    () => [
      {
        name: 'Order No',
        sortable: true,
        sortValue: (o: Order) => o.orderNumber,
        selector: (o: Order) => (
          <span className="font-mono text-xs font-bold text-[var(--gold-main)]">{o.orderNumber}</span>
        ),
      },
      {
        name: 'Customer',
        sortable: true,
        sortValue: (o: Order) => o.customerName || '',
        selector: (o: Order) => o.customerName || '—',
      },
      {
        name: 'Phone',
        selector: (o: Order) => (
          <span className="font-mono text-[var(--text-muted)]">{o.customerPhone || '—'}</span>
        ),
      },
      {
        name: 'City',
        selector: (o: Order) => o.shippingCity || '—',
      },
      {
        name: 'Tracking',
        selector: (o: Order) => (
          <span className="font-mono text-[10px] text-[var(--text-faint)]">{o.trackingId || '—'}</span>
        ),
      },
      {
        name: 'Status',
        cell: (o: Order) => <span className={`badge badge-${o.status}`}>{statusLabel(o.status)}</span>,
      },
      {
        name: 'Total',
        sortable: true,
        sortValue: (o: Order) => o.total,
        selector: (o: Order) => (
          <span className="font-title font-bold">{formatCurrency(o.total)}</span>
        ),
      },
      {
        name: 'Date',
        sortable: true,
        sortValue: (o: Order) => o.createdAt,
        selector: (o: Order) => formatOrderDate(o.createdAt),
      },
      {
        name: 'Actions',
        right: true,
        cell: (o: Order) => (
          <div className="flex items-center justify-end gap-1.5">
            <Link
              to="/admin/orders/$orderId"
              params={{ orderId: o.id }}
              className="p-2 rounded-lg hover:bg-[var(--bg-surface-2)] text-[var(--text-muted)] hover:text-[var(--gold-main)]"
              title="View order"
            >
              <Eye className="w-4 h-4" />
            </Link>
            <Link
              to="/admin/orders/$orderId/status"
              params={{ orderId: o.id }}
              className="p-2 rounded-lg hover:bg-[var(--gold-soft-bg)] text-[var(--text-muted)] hover:text-[var(--gold-main)]"
              title="Update status"
            >
              <Truck className="w-4 h-4" />
            </Link>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      <div>
        <h2 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] flex items-center gap-2">
          <ShoppingBag className="w-6 h-6 text-[var(--gold-main)] shrink-0" />
          Order Management
        </h2>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          Review orders, update dispatch timeline & tracking IDs.
          {total > 0 && <span className="text-[var(--gold-main)] ml-1">({total} total)</span>}
        </p>
      </div>

      {/* Single-line filters */}
      <div className="glass-luxury-card p-3 sm:p-4 overflow-visible">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative shrink-0 w-[min(100%,220px)] sm:w-52">
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              placeholder="Order #, name, phone..."
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-2.5 pl-9 pr-3 text-xs text-[var(--text-primary)] outline-none"
            />
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-[var(--gold-main)] pointer-events-none" />
          </div>

          <SearchableSelect
            value={customerFilter}
            onChange={(v) => { setCustomerFilter(v); setPage(1) }}
            options={customerOptions}
            placeholder="All customers"
            aria-label="Filter by customer"
            className="shrink-0 w-36 sm:w-48"
          />

          <SearchableSelect
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1) }}
            options={statusOptions}
            placeholder="All statuses"
            aria-label="Filter by status"
            className="shrink-0 w-32 sm:w-36"
          />

          <input
            type="text"
            value={trackingFilter}
            onChange={(e) => { setTrackingFilter(e.target.value); setPage(1) }}
            placeholder="Tracking ID"
            className="shrink-0 w-28 sm:w-36 bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2.5 px-3 text-xs text-[var(--text-primary)] outline-none font-mono"
          />

          <input
            type="text"
            value={cityFilter}
            onChange={(e) => { setCityFilter(e.target.value); setPage(1) }}
            placeholder="City"
            className="shrink-0 w-24 sm:w-28 bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2.5 px-3 text-xs text-[var(--text-primary)] outline-none"
          />

          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1) }}
            className="shrink-0 w-[8.5rem] bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2.5 px-2 text-xs text-[var(--text-primary)] outline-none"
            aria-label="From date"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1) }}
            className="shrink-0 w-[8.5rem] bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2.5 px-2 text-xs text-[var(--text-primary)] outline-none"
            aria-label="To date"
          />

          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="shrink-0 p-2.5 rounded-xl border border-[var(--border)] text-[var(--text-muted)] hover:text-red-400 hover:border-red-500/30 transition"
              title="Clear filters"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {selectedCustomer && (
        <div className="glass-luxury-card p-4 text-xs grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 border border-[var(--gold-soft-border)]">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-1">Customer</p>
            <p className="font-semibold text-[var(--text-primary)]">{selectedCustomer.name}</p>
            <p className="font-mono text-[var(--gold-main)] mt-0.5">{selectedCustomer.phone}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-1">Address</p>
            <p className="text-[var(--text-muted)]">
              {[selectedCustomer.addressLine1, selectedCustomer.city, selectedCustomer.pincode].filter(Boolean).join(', ') || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-1">Lifetime value</p>
            <p className="font-title font-bold text-[var(--text-primary)]">{formatCurrency(selectedCustomer.totalSpent)}</p>
            <p className="text-[var(--text-muted)] mt-0.5">{selectedCustomer.totalOrders} order(s)</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-1">Last order</p>
            <p className="text-[var(--text-muted)]">{formatOrderDate(selectedCustomer.lastOrderAt ?? undefined)}</p>
          </div>
        </div>
      )}

      {/* Desktop table */}
      <div className="glass-luxury-card overflow-hidden hidden lg:block">
        <AppDataTable
          columns={orderColumns}
          data={orders}
          keyField={(o) => o.id}
          progressPending={loading}
          noDataComponent="No orders match your filters."
        />
      </div>

      {/* Tablet / mobile cards */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <div className="glass-luxury-card p-8 text-center">
            <div className="w-8 h-8 border-2 border-[var(--gold-main)] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : orders.length === 0 ? (
          <div className="glass-luxury-card p-8 text-center text-xs text-[var(--text-muted)]">
            No orders match your filters.
          </div>
        ) : (
          orders.map((o) => (
            <div key={o.id} className="glass-luxury-card p-4 space-y-3">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <p className="font-mono text-sm font-bold text-[var(--gold-main)]">{o.orderNumber}</p>
                  <p className="text-sm font-medium text-[var(--text-primary)] mt-0.5 truncate">{o.customerName || '—'}</p>
                  <p className="font-mono text-[10px] text-[var(--text-muted)] mt-0.5">{o.customerPhone || '—'}</p>
                </div>
                <span className={`badge badge-${o.status} shrink-0`}>{statusLabel(o.status)}</span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-0.5">City</p>
                  <p className="text-[var(--text-primary)]">{o.shippingCity || '—'}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-0.5">Total</p>
                  <p className="font-title font-bold text-[var(--text-primary)]">{formatCurrency(o.total)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-0.5">Tracking</p>
                  <p className="font-mono text-[10px] text-[var(--text-muted)] break-all">{o.trackingId || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-0.5">Date</p>
                  <p className="text-[var(--text-muted)]">{formatOrderDate(o.createdAt)}</p>
                </div>
              </div>

              <div className="flex gap-2 pt-1 border-t border-[var(--border)]/50">
                <Link
                  to="/admin/orders/$orderId"
                  params={{ orderId: o.id }}
                  className="flex-1 btn-gold-outline text-xs py-2.5 flex items-center justify-center gap-1.5 min-h-[44px]"
                >
                  <Eye className="w-4 h-4" /> View
                </Link>
                <Link
                  to="/admin/orders/$orderId/status"
                  params={{ orderId: o.id }}
                  className="flex-1 btn-gold text-xs py-2.5 flex items-center justify-center gap-1.5 min-h-[44px]"
                >
                  <Truck className="w-4 h-4" /> Update
                </Link>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <DataTablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={10}
        onPageChange={setPage}
        className="pt-2"
      />
    </div>
  )
}
